import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Input,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import dayjs from "dayjs";
import { commercialProposalApi, downloadProposalPdf } from "../../api/commercialProposalApi";
import {
  CommercialProposalStatus,
  ICommercialProposal,
} from "../../types/commercialProposal.type";
import { useAuth } from "../../contexts/auth.context";
import { hasAdminAccess } from "../../utils/roleUtils";
import { api } from "../../api/api";

const { RangePicker } = DatePicker;

const STATUS_COLORS: Record<CommercialProposalStatus, string> = {
  [CommercialProposalStatus.Draft]: "default",
  [CommercialProposalStatus.Created]: "green",
  [CommercialProposalStatus.Sent]: "blue",
  [CommercialProposalStatus.Completed]: "purple",
  [CommercialProposalStatus.Expired]: "red",
};

const EDITABLE_STATUSES = [CommercialProposalStatus.Draft, CommercialProposalStatus.Created];

/** Статусы, при которых КП не считается просроченным. */
const NON_OVERDUE_STATUSES = [
  CommercialProposalStatus.Draft,
  CommercialProposalStatus.Completed,
  CommercialProposalStatus.Expired,
];

/** Просрочено: дедлайн прошёл, статус не терминальный/незапущенный. */
function isOverdue(proposal: ICommercialProposal): boolean {
  if (!proposal.dueDate) return false;
  if (NON_OVERDUE_STATUSES.includes(proposal.status)) return false;
  return new Date(proposal.dueDate).getTime() < Date.now();
}

export default function CommercialProposalsPage() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const role = authState.user?.role;
  const isAdmin = hasAdminAccess(role) || role === "director" || role === "manager";

  const [proposals, setProposals] = useState<ICommercialProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState("createdAt");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [salesUsers, setSalesUsers] = useState<Array<{ _id: string; name: string }>>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Debounce поиска
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchText]);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {
        page,
        limit,
        sortBy,
        order,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter.length > 0) params.status = statusFilter;
      if (ownerFilter) params.ownerId = ownerFilter;
      if (overdueOnly) params.overdue = "true";
      if (dateRange?.[0]) params.dateFrom = dateRange[0].format("YYYY-MM-DD");
      if (dateRange?.[1]) params.dateTo = dateRange[1].format("YYYY-MM-DD");

      const { data } = await commercialProposalApi.getAll(params);
      setProposals(data.proposals);
      setTotal(data.total);
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Ошибка при загрузке КП");
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, order, debouncedSearch, statusFilter, ownerFilter, overdueOnly, dateRange]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  // Список продажников для фильтра (только для админов)
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/crm/sales-users")
      .then(({ data }) => {
        const users = Array.isArray(data) ? data : data.users || [];
        setSalesUsers(users.map((u: any) => ({ _id: u._id, name: u.name })));
      })
      .catch(() => {
        // Фильтр по менеджерам необязателен — молча пропускаем
      });
  }, [isAdmin]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICommercialProposal> | SorterResult<ICommercialProposal>[]
  ) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 25);
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (s?.field && s.order) {
      setSortBy(String(s.field));
      setOrder(s.order === "ascend" ? "asc" : "desc");
    } else {
      setSortBy("createdAt");
      setOrder("desc");
    }
  };

  const handleDownload = async (proposal: ICommercialProposal) => {
    setDownloadingId(proposal._id);
    try {
      await downloadProposalPdf(proposal._id, proposal.proposalNumber);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (proposal: ICommercialProposal) => {
    setDeletingId(proposal._id);
    try {
      await commercialProposalApi.delete(proposal._id);
      message.success("КП удалено");
      fetchProposals();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Ошибка при удалении КП");
    } finally {
      setDeletingId(null);
    }
  };

  const formatMoney = (value: number, currency: string) =>
    `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(value)} ${currency}`;

  const columns: ColumnsType<ICommercialProposal> = useMemo(
    () => [
      {
        title: "№ КП",
        dataIndex: "proposalNumber",
        key: "proposalNumber",
        sorter: true,
        width: 110,
        render: (num: number, record) => (
          <b>
            КП-{String(num).padStart(4, "0")}
            {record.version > 1 ? `/${record.version}` : ""}
          </b>
        ),
      },
      {
        title: "Дата",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        width: 110,
        render: (value: string) => dayjs(value).format("DD.MM.YYYY"),
      },
      {
        title: "Клиент",
        dataIndex: "clientName",
        key: "clientName",
        sorter: true,
        ellipsis: true,
      },
      {
        title: "Менеджер",
        dataIndex: "ownerName",
        key: "ownerName",
        ellipsis: true,
      },
      {
        title: "Позиций",
        key: "itemsCount",
        width: 90,
        align: "center" as const,
        render: (_: unknown, record) => record.items.length,
      },
      {
        title: "Сумма",
        dataIndex: "totalAmount",
        key: "totalAmount",
        sorter: true,
        align: "right" as const,
        width: 160,
        render: (value: number, record) => <b>{formatMoney(value, record.currency)}</b>,
      },
      {
        title: "Срок",
        dataIndex: "validityDays",
        key: "validityDays",
        width: 80,
        align: "center" as const,
        render: (days: number) => `${days} дн.`,
      },
      {
        title: "Действует до",
        dataIndex: "validUntil",
        key: "validUntil",
        sorter: true,
        width: 120,
        render: (value: string) => dayjs(value).format("DD.MM.YYYY"),
      },
      {
        title: "Срок вып. / дедлайн",
        key: "dueDate",
        dataIndex: "dueDate",
        sorter: true,
        width: 150,
        render: (_: unknown, record) => {
          if (!record.dueDate)
            return <span style={{ color: "#bbb" }}>—</span>;
          const overdue = isOverdue(record);
          return (
            <span style={{ color: overdue ? "#cf1322" : undefined, fontWeight: overdue ? 600 : undefined }}>
              {record.leadTimeDays} дн. · {dayjs(record.dueDate).format("DD.MM.YYYY")}
              {overdue && " ⚠"}
            </span>
          );
        },
      },
      {
        title: "Статус",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status: CommercialProposalStatus) => (
          <Tag color={STATUS_COLORS[status]}>{status}</Tag>
        ),
      },
      {
        title: "Действия",
        key: "actions",
        width: 160,
        render: (_: unknown, record) => (
          <Space size="small">
            <Tooltip title="Просмотр">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/commercial-proposals/${record._id}`)}
              />
            </Tooltip>
            {EDITABLE_STATUSES.includes(record.status) && (
              <Tooltip title="Редактировать">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/commercial-proposals/${record._id}/edit`)}
                />
              </Tooltip>
            )}
            <Tooltip title="Скачать PDF">
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={downloadingId === record._id}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
            {(record.status === CommercialProposalStatus.Draft || hasAdminAccess(role)) && (
              <Popconfirm
                title="Удалить КП?"
                okText="Удалить"
                cancelText="Отмена"
                onConfirm={() => handleDelete(record)}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingId === record._id}
                />
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [downloadingId, deletingId, role]
  );

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <h2 style={{ margin: 0 }}>Коммерческие предложения</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/commercial-proposals/create")}
        >
          Создать КП
        </Button>
      </div>

      <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <Input.Search
          placeholder="Поиск: № КП, клиент, менеджер, продукция"
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 320 }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Статус"
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          style={{ minWidth: 180 }}
          options={Object.values(CommercialProposalStatus).map((status) => ({
            value: status,
            label: status,
          }))}
        />
        {isAdmin && (
          <Select
            allowClear
            showSearch
            placeholder="Менеджер"
            value={ownerFilter}
            onChange={(value) => {
              setOwnerFilter(value);
              setPage(1);
            }}
            style={{ minWidth: 200 }}
            optionFilterProp="label"
            options={salesUsers.map((user) => ({ value: user._id, label: user.name }))}
          />
        )}
        <RangePicker
          value={dateRange}
          format="DD.MM.YYYY"
          onChange={(value) => {
            setDateRange(value);
            setPage(1);
          }}
        />
        <Button
          danger={overdueOnly}
          type={overdueOnly ? "primary" : "default"}
          onClick={() => {
            setOverdueOnly((prev) => !prev);
            setPage(1);
          }}
        >
          Просроченные
        </Button>
        <Tooltip title="Обновить">
          <Button icon={<ReloadOutlined />} onClick={fetchProposals} />
        </Tooltip>
      </Space>

      <Table<ICommercialProposal>
        rowKey="_id"
        columns={columns}
        dataSource={proposals}
        loading={loading}
        onChange={handleTableChange}
        rowClassName={(record) => (isOverdue(record) ? "proposal-row-overdue" : "")}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
          showTotal: (t) => `Всего: ${t}`,
        }}
        scroll={{ x: 1250 }}
        locale={{ emptyText: "Коммерческих предложений пока нет" }}
      />
      <style>{`.proposal-row-overdue > td { background: #fff1f0 !important; }`}</style>
    </div>
  );
}
